import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, Mail, Grid3x3, Bell, MessageCircle, LogOut, Star, Coins, Zap, Trophy, Plus, Sparkles, ArrowUp } from 'lucide-react';
import Logo from '../Logo';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import BroadcastBanner from '../BroadcastBanner';
import BuyStars from '../BuyStars';
import SpinWheel from '../SpinWheel';
import InstallPwaButton from '../InstallPwaButton';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { COSMIC_RANKS } from '../../mock/mock';
import VipBadge from '../VipBadge';
import FirstLoginWelcome from '../FirstLoginWelcome';
import LiveNotifier from '../LiveNotifier';

const BOTTOM_NAV = [
  { to: '/app', icon: Home, label: 'Home', exact: true },
  { to: '/app/messages', icon: Mail, label: 'Mail', badgeKey: 'messages' },
  { to: '/app/menu', icon: Grid3x3, label: 'Menu' },
  { to: '/app/notifications', icon: Bell, label: 'Notifications', badgeKey: 'notifications' },
  { to: '/app/planets', icon: MessageCircle, label: 'Chat' },
];

export default function AppShell() {
  const loc = useLocation();
  const nav = useNavigate();
  const { user, logout, buyStarsOpen, setBuyStarsOpen, counts, refreshCounts, chatFocused } = useAuth();
  const buyOpen = buyStarsOpen;
  const setBuyOpen = setBuyStarsOpen;
  const [spinOpen, setSpinOpen] = useState(false);

  // One-shot welcome overlay — appears once per user, keyed by a version tag
  // so we can re-surface the welcome for EVERY user when the intro is
  // meaningfully refreshed (bump the version below). Users who dismissed the
  // previous version still see the new one once, then it stays out of the way.
  const [showWelcome, setShowWelcome] = useState(false);
  useEffect(() => {
    if (!user?.user_id) return;
    try {
      if (!localStorage.getItem(`wv_welcome_seen_v2_${user.user_id}`)) {
        setShowWelcome(true);
      }
    } catch { /* ignore access errors (private mode, etc.) */ }
  }, [user?.user_id]);

  // Top-Up coach mark — visible until (a) the traveler taps the Top-Up
  // pill, (b) hovers over the tip or the pill (soft acknowledge), or
  // (c) 8s passes automatically. Tap = permanent dismiss (localStorage);
  // hover/timeout = this-session only, so the tip returns on reload if
  // the traveler still hasn't tapped Top-Up.
  const TOPUP_HINT_VERSION = 'v2';
  const TOPUP_HINT_AUTO_HIDE_MS = 8000;
  const topupHintKey = user?.user_id ? `wv_topup_tapped_${TOPUP_HINT_VERSION}_${user.user_id}` : null;
  const [showTopupHint, setShowTopupHint] = useState(false);
  useEffect(() => {
    if (!topupHintKey) return undefined;
    let dismissed = false;
    try { dismissed = localStorage.getItem(topupHintKey) === '1'; } catch { /* ignore */ }
    if (dismissed) return undefined;
    const t1 = setTimeout(() => setShowTopupHint(true), 1200);
    // Auto-hide after AUTO_HIDE_MS from mount (i.e. hint shows for ~8s
    // then fades away on its own).
    const t2 = setTimeout(() => setShowTopupHint(false), 1200 + TOPUP_HINT_AUTO_HIDE_MS);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [topupHintKey]);
  // Permanently dismiss on FIRST click — travelers who know where Top-Up
  // is don't need the coach mark badgering them again.
  const dismissTopupHint = () => {
    setShowTopupHint(false);
    if (topupHintKey) {
      try { localStorage.setItem(topupHintKey, '1'); } catch { /* ignore */ }
    }
  };
  // Soft-dismiss on hover / touch of the tip or the pill — hides for the
  // rest of THIS session but returns on next reload if not tapped yet.
  const softHideTopupHint = () => setShowTopupHint(false);

  // Refs to the Top-Up pill (desktop + mobile) so the coach mark can be
  // positioned to point EXACTLY under whichever one is currently rendered.
  // Without this the arrow floats near the right edge while the pill sits
  // far to the left — the user reported this as a bug.
  const topupDesktopRef = useRef(null);
  const topupMobileRef = useRef(null);
  const [hintPos, setHintPos] = useState(null); // { left, top } in px

  useLayoutEffect(() => {
    if (!showTopupHint) return undefined;
    const measure = () => {
      const el = topupDesktopRef.current?.offsetParent
        ? topupDesktopRef.current
        : topupMobileRef.current;
      if (!el) { setHintPos(null); return; }
      const r = el.getBoundingClientRect();
      setHintPos({
        // Center the arrow tip under the middle of the pill
        left: Math.round(r.left + r.width / 2),
        top: Math.round(r.bottom + 12),
      });
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, { passive: true });
    // Re-measure once fonts / layout settle
    const t = setTimeout(measure, 250);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure);
    };
  }, [showTopupHint, user?.stars]);

  // Refresh counts when the user navigates (covers the case where you visit /messages or /notifications)
  useEffect(() => { refreshCounts?.(); }, [loc.pathname, refreshCounts]);

  // counts now come from AuthContext — no per-page polling needed here

  if (!user) return null;
  const rank = COSMIC_RANKS.find(r => r.rank === user.rank) || COSMIC_RANKS[0];
  const next = COSMIC_RANKS.find(r => r.rank === user.rank + 1);
  const progress = next ? Math.min(100, (((user.xp || 0) - rank.xp) / Math.max(1, next.xp - rank.xp)) * 100) : 100;
  const activityColor = user.activity_color || '#94A3B8';
  const activityName = user.activity_name || 'Newcomer';

  const isActive = (item) => item.exact ? loc.pathname === item.to : loc.pathname === item.to || (item.to !== '/app' && loc.pathname.startsWith(item.to));

  // Focused-chat pages: hide the bottom nav and let the chat composer sit
  // flush against the viewport bottom (otherwise the nav covers the input).
  // We use the URL where possible AND a context flag for state-routed pages
  // (Messages.jsx switches threads via local state, not URL params).
  const isPlanetChat = /^\/app\/planets\/[^/]+$/.test(loc.pathname);
  const isDmChat    = /^\/app\/messages\/[^/]+$/.test(loc.pathname);
  const focusedChat = isPlanetChat || isDmChat || chatFocused;

  return (
    <div className={`min-h-screen cosmic-bg relative ${focusedChat ? '' : 'pb-24'}`}>
      <div className="stars-bg" />

      {/* Top Bar */}
      <header className="sticky top-0 z-40 glass border-b border-white/5">
        <div className="flex items-center justify-between px-4 lg:px-6 h-16">
          <Link to="/app" className="flex items-center gap-2.5 group">
            <div className="relative">
              <div className="absolute -inset-1 rounded-2xl blur-lg opacity-60 group-hover:opacity-90 transition" style={{ background: 'linear-gradient(135deg,#22D3EE,#8B5CF6,#EC4899)' }} />
              <img src="/logo.jpeg" alt="WorldVibe" className="relative w-14 h-14 rounded-2xl object-cover ring-2 ring-white/20 group-hover:ring-cyan-400/50 transition" />
            </div>
            <span className="font-display font-bold tracking-tight text-2xl">
              <span className="text-white">World</span>
              <span style={{ background: 'linear-gradient(135deg,#22D3EE,#8B5CF6,#EC4899)', WebkitBackgroundClip:'text', backgroundClip:'text', color:'transparent'}}>Vibe</span>
            </span>
          </Link>
          <div className="flex items-center gap-2 md:gap-3 ml-auto">
            <TooltipProvider delayDuration={200}>
            <div className="hidden sm:flex items-center gap-2 text-sm">
              {/* STARS = premium IAP currency. Amber-gold pill with explicit
                  "TOP UP" label so travelers immediately understand this is
                  the buy-with-money entry point. */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    ref={topupDesktopRef}
                    onClick={(e) => { e.preventDefault(); setBuyOpen(true); dismissTopupHint(); }}
                    onMouseEnter={softHideTopupHint}
                    data-testid="open-buy-stars-desktop"
                    className="relative flex items-center gap-1.5 pl-3 pr-1 py-1.5 rounded-full border border-amber-300/40 bg-gradient-to-r from-amber-400/20 to-orange-500/15 hover:from-amber-400/30 hover:to-orange-500/25 shadow-sm shadow-amber-500/10 transition group"
                    style={showTopupHint ? { animation: 'topup-pulse 1.8s ease-in-out infinite' } : undefined}
                  >
                    <Star size={14} fill="#FBBF24" className="text-amber-300" />
                    <span className="font-semibold text-amber-100">{user.stars?.toLocaleString() || 0}</span>
                    <span className="ml-1 inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r from-amber-400 to-orange-500 text-black shadow-sm">
                      <Plus size={10} strokeWidth={3} /> Top up
                    </span>
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[220px]">
                  <div className="font-semibold text-amber-300 mb-0.5">Top up Stars</div>
                  <div className="text-[11px] text-white/70 leading-snug">
                    Stars are the premium currency for gifts, rare pets & VIP. Purchase via secure IAP.
                  </div>
                </TooltipContent>
              </Tooltip>

              {/* CREDITS = free/earned spin currency. Cyan pill with explicit
                  "SPIN" label so travelers don't confuse it with the star
                  IAP button next to it. */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setSpinOpen(true)}
                    data-testid="open-spin-wheel"
                    className="flex items-center gap-1.5 pl-3 pr-1 py-1.5 rounded-full border border-cyan-300/30 bg-cyan-500/10 hover:bg-cyan-500/20 transition"
                  >
                    <Coins size={14} className="text-cyan-300" />
                    <span className="font-semibold text-cyan-100">{user.credits?.toLocaleString() || 0}</span>
                    <span className="ml-1 inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-cyan-400/25 text-cyan-100 border border-cyan-300/30">
                      <Sparkles size={9} /> Spin
                    </span>
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[220px]">
                  <div className="font-semibold text-cyan-300 mb-0.5">Free Credits · Daily Spin</div>
                  <div className="text-[11px] text-white/70 leading-snug">
                    Earned in-app — spin daily for stars, XP boosts and rare pet drops.
                  </div>
                </TooltipContent>
              </Tooltip>

              {/* XP = status read-out, NOT a purchase button. Compact chip. */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-full cursor-help"
                    data-testid="activity-tier-chip"
                    style={{ color: activityColor, background: `${activityColor}12`, border: `1px solid ${activityColor}30` }}
                  >
                    <Zap size={14} />
                    <span className="font-semibold">{user.xp?.toLocaleString() || 0}</span>
                    <span className="ml-1 text-[10px] font-bold uppercase tracking-wider opacity-80">{activityName}</span>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[220px]">
                  <div className="font-semibold mb-0.5" style={{ color: activityColor }}>Activity tier · {activityName}</div>
                  <div className="text-[11px] text-white/70 leading-snug">
                    Earn XP by chatting, completing quests & sending gifts. Higher tiers unlock cosmetic perks.
                  </div>
                </TooltipContent>
              </Tooltip>
            </div>
            </TooltipProvider>
            <button onClick={() => nav('/app/profile')} className="flex items-center gap-2 group relative">
              <Avatar className="w-9 h-9 ring-2 transition" style={{ boxShadow: `0 0 0 2px ${rank.color}` }}>
                <AvatarImage src={user.avatar} />
                <AvatarFallback className="bg-fuchsia-500/30">{user.displayName?.slice(0,2).toUpperCase()}</AvatarFallback>
              </Avatar>
              {user.is_vip && (
                <VipBadge
                  active
                  size="sm"
                  animated
                  data-testid="shell-vip-badge"
                  className="absolute -bottom-1 -right-1"
                />
              )}
            </button>
            <button onClick={logout} title="Logout" className="p-2 text-white/60 hover:text-white"><LogOut size={18} /></button>
          </div>
        </div>
        {/* Mobile rank progress */}
        <div className="sm:hidden px-4 pb-2">
          <div className="flex items-center justify-between text-[11px]">
            <span className="flex items-center gap-1" style={{ color: rank.color }}><Trophy size={11} />{rank.name}</span>
            <span className="flex items-center gap-2 text-white/60">
              <button
                ref={topupMobileRef}
                type="button"
                onClick={(e) => { e.preventDefault(); setBuyOpen(true); dismissTopupHint(); }}
                onMouseEnter={softHideTopupHint}
                data-testid="open-buy-stars-mobile"
                className="relative flex items-center gap-1 pl-2 pr-1 py-1 rounded-full border border-amber-300/40 bg-gradient-to-r from-amber-400/20 to-orange-500/15 text-amber-100"
                style={showTopupHint ? { animation: 'topup-pulse 1.8s ease-in-out infinite' } : undefined}
              >
                <Star size={11} fill="#FBBF24" className="text-amber-300" />
                <span className="font-semibold">{user.stars?.toLocaleString() || 0}</span>
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-gradient-to-r from-amber-400 to-orange-500 text-black">
                  <Plus size={8} strokeWidth={3} /> Top up
                </span>
              </button>
              <button
                onClick={() => setSpinOpen(true)}
                data-testid="open-spin-wheel-mobile"
                className="flex items-center gap-1 pl-2 pr-1 py-1 rounded-full border border-cyan-300/30 bg-cyan-500/10 text-cyan-100"
              >
                <Coins size={11} className="text-cyan-300" />
                <span className="font-semibold">{user.credits?.toLocaleString() || 0}</span>
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-cyan-400/25 text-cyan-100 border border-cyan-300/30">
                  <Sparkles size={8} /> Spin
                </span>
              </button>
            </span>
          </div>
          <div className="mt-1 h-1 bg-white/8 rounded-full overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${progress}%`, background: 'linear-gradient(90deg,#00C6FF,#7B2FF7,#00F5D4)' }} />
          </div>
        </div>
      </header>

      {/* Coach mark — dynamically positioned so the arrow tip lands under
          the actual Top-Up pill (desktop OR mobile). Compact on mobile so
          it doesn't overlap the currency chips or run off-screen. */}
      {showTopupHint && hintPos && (() => {
        // Responsive width — narrower on small screens where horizontal
        // space is tight. 190 keeps the pill row breathable at 390px.
        const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
        const isMobile = vw < 640;
        const TIP_W = isMobile ? 190 : 240;
        // Arrow right offset inside tooltip (right-4 = 16px) + half arrow size (13)
        const ARROW_INSET = 16 + 13;
        // Position tooltip so its arrow tip sits under the pill centre
        const desiredLeft = hintPos.left - (TIP_W - ARROW_INSET);
        // Viewport clamp — keep at least 8px margin on either side
        const left = Math.max(8, Math.min(desiredLeft, vw - TIP_W - 8));
        // If we had to clamp, recompute where the arrow should sit inside
        // the tooltip so it still points at the pill centre
        const arrowLeft = Math.max(12, Math.min(TIP_W - 12, hintPos.left - left));
        return (
          <div
            className="pointer-events-none fixed z-[55]"
            data-testid="topup-coach-mark"
            style={{ top: hintPos.top, left, width: TIP_W, animation: 'topup-hint-in 300ms ease-out' }}
          >
            <div
              className="pointer-events-auto relative rounded-2xl border border-amber-300/50 bg-gradient-to-br from-amber-500/25 to-orange-600/20 backdrop-blur-md px-3 py-2 sm:px-3.5 sm:py-2.5 shadow-xl shadow-amber-500/25"
              onMouseEnter={softHideTopupHint}
              onTouchStart={softHideTopupHint}
            >
              <div className="flex items-center gap-1.5 text-[9px] sm:text-[10px] uppercase tracking-widest text-amber-200 font-bold">
                <Sparkles size={10} /> Tip
              </div>
              <div className="mt-0.5 text-[11px] sm:text-xs text-white leading-snug">
                Tap <span className="font-bold text-amber-200">Top up</span> to buy Stars for gifts, VIP & pets.
              </div>
              <ArrowUp
                size={isMobile ? 20 : 26}
                className="absolute text-amber-300 drop-shadow-[0_2px_6px_rgba(251,191,36,0.7)] pointer-events-none"
                strokeWidth={3}
                style={{
                  top: isMobile ? -14 : -16,
                  left: arrowLeft - (isMobile ? 10 : 13),
                  animation: 'topup-arrow-bounce 1s ease-in-out infinite',
                }}
              />
            </div>
          </div>
        );
      })()}

      <main className="px-4 lg:px-8 py-5 relative max-w-6xl mx-auto">
        <Outlet />
      </main>

      <BroadcastBanner />
      <BuyStars open={buyOpen} onOpenChange={setBuyOpen} />
      <SpinWheel open={spinOpen} onOpenChange={setSpinOpen} />
      {showWelcome && <FirstLoginWelcome user={user} onDismiss={() => setShowWelcome(false)} />}
      <InstallPwaButton withBottomNav={!focusedChat} />
      <LiveNotifier />

      {/* Bottom Nav (mobile + desktop) — hidden on focused chat pages so the
          message composer is not covered by the icon row. */}
      {!focusedChat && (
        <nav
          className="fixed bottom-0 left-0 right-0 z-40 glass border-t border-white/5"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
        <div className="max-w-3xl mx-auto grid grid-cols-5">
          {BOTTOM_NAV.map(item => {
            const Icon = item.icon;
            const active = isActive(item);
            const badge = item.badgeKey ? counts[item.badgeKey] : 0;
            return (
              <Link key={item.to} to={item.to} className={`relative flex flex-col items-center gap-1 py-2.5 ${active ? 'text-cyan-400' : 'text-white/60 hover:text-white'}`}>
                <div className="relative">
                  <Icon size={22} />
                  {badge > 0 && (
                    <Badge className="absolute -top-2 -right-3 h-4 min-w-4 px-1 bg-rose-500 text-white border-0 text-[10px] rounded-full">{badge > 99 ? '99' : badge}</Badge>
                  )}
                </div>
                <span className="text-[10px] font-medium">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
      )}

      {/* Keyframes for the Top-Up coach mark + pulse ring on the pill. */}
      <style>{`
        @keyframes topup-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(251, 191, 36, 0.55); }
          50%      { box-shadow: 0 0 0 8px rgba(251, 191, 36, 0); }
        }
        @keyframes topup-arrow-bounce {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-5px); }
        }
        @keyframes topup-hint-in {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
